import { z, type ZodType } from 'zod'

import type { HttpClient } from './http-client'
import { MAX_PAGE_SIZE, type PageParams, type Paged } from './pagination'

export interface ResourceEndpoints {
  /** e.g. `/api/v1/users`, for listing and creating. */
  collection: string
  /** e.g. `(id) => /api/v1/users/${id}`, for reading, updating and deleting one. */
  item: (id: string) => string
}

export interface ReadSchemas<TDto> {
  item: ZodType<TDto>
  page: ZodType<Paged<TDto>>
}

export interface ResourceSchemas<TDto, TCreated = TDto> extends ReadSchemas<TDto> {
  /** Only when creating answers with something other than the plain item (an account also returns its first user). */
  created?: ZodType<TCreated>
}

/** A safety stop, so a server that ignores `page` can never keep `listAll` running. */
const MAX_PAGES = 100

/**
 * The reads every resource has: list a page, list all pages, get one. For a resource the app only looks at (the backend's
 * background tasks), extend this; for one it also changes, extend `CrudService`.
 */
export abstract class ReadService<TDto, TFilters extends object = object> {
  protected readonly http: HttpClient
  protected readonly endpoints: ResourceEndpoints
  protected readonly schemas: ReadSchemas<TDto>

  protected constructor(http: HttpClient, endpoints: ResourceEndpoints, schemas: ReadSchemas<TDto>) {
    this.http = http
    this.endpoints = endpoints
    this.schemas = schemas
  }

  /** One page. Filters become query-string values. */
  list(filters: TFilters & PageParams = {} as TFilters, signal?: AbortSignal): Promise<Paged<TDto>> {
    return this.http.get(this.endpoints.collection, this.schemas.page, { query: filters as Record<string, string | number | boolean | undefined>, signal })
  }

  /** Every page, in order, as one array. Use for lists small enough to hold whole (accounts, a company's users). */
  async listAll(filters: TFilters = {} as TFilters, signal?: AbortSignal): Promise<TDto[]> {
    const items: TDto[] = []
    for (let page = 1; page <= MAX_PAGES; page++) {
      const result = await this.list({ ...filters, page, pageSize: MAX_PAGE_SIZE }, signal)
      items.push(...result.items)
      if (result.items.length === 0 || items.length >= result.totalCount) break
    }
    return items
  }

  get(id: string, signal?: AbortSignal): Promise<TDto> {
    return this.http.get(this.endpoints.item(id), this.schemas.item, { signal })
  }
}

/**
 * Adds the writes: create, update, remove. A module's service extends this with its own types and adds whatever else its
 * resource offers. Services return plain data and throw `ApiError`; they know nothing about React or caching (the module's
 * `*.queries.ts` adds that).
 *
 *   class UsersService extends CrudService<UserDto, UserCreateBody, UserUpdateBody, UserFilters> { ... }
 */
export abstract class CrudService<TDto, TCreate, TUpdate, TFilters extends object = object, TCreated = TDto> extends ReadService<TDto, TFilters> {
  private readonly createdSchema: ZodType<TCreated>

  protected constructor(http: HttpClient, endpoints: ResourceEndpoints, schemas: ResourceSchemas<TDto, TCreated>) {
    super(http, endpoints, schemas)
    this.createdSchema = (schemas.created ?? schemas.item) as ZodType<TCreated>
  }

  create(body: TCreate, signal?: AbortSignal): Promise<TCreated> {
    return this.http.post(this.endpoints.collection, this.createdSchema, { body, signal })
  }

  update(id: string, body: TUpdate, signal?: AbortSignal): Promise<TDto> {
    return this.http.put(this.endpoints.item(id), this.schemas.item, { body, signal })
  }

  /** The backend answers 204 with no body. */
  async remove(id: string, signal?: AbortSignal): Promise<void> {
    await this.http.delete(this.endpoints.item(id), z.null(), { signal })
  }
}
